import {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";

import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Loader from "../../../components/Loader";
import { Select } from "../../../components/OldSelect";
import { AuthContext } from "../../../contexts/AuthContext";
import {
    getAppSettings,
    patchAppSettings,
} from "../../../services/api/admin/settings";
import {
    AppSettings,
    GraduationRoleThresholds,
} from "../../../types/api/admin/settings";
import { useUnsavedChanges } from "../../../contexts/UnsavedChangesContext";

import styles from "./styles.module.scss";

type Props = {
    handleTabChange: (key: string) => void;
};

type RoleKey = keyof AppSettings["graduationByRole"];

const ROLE_LABELS: Record<RoleKey, string> = {
    Junior: "Junior → Expert",
    Expert: "Expert → Pro",
    Pro: "Pro → Indépendant",
    "Indépendant": "Indépendant → Formé",
};

const Progression: FC<Props> = ({ handleTabChange }) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const { hasPermission } = useContext(AuthContext)!;
    const { registerUnsavedGuard } = useUnsavedChanges();
    const canReadSettings = hasPermission("admin-settings", "read");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [graduationByRole, setGraduationByRole] = useState<
        AppSettings["graduationByRole"] | null
    >(null);
    const [baseline, setBaseline] = useState<string | null>(null);

    useEffect(() => {
        getAppSettings()
            .then((settings) => {
                if (!settings) {
                    throw new Error("EMPTY_SETTINGS");
                }
                setGraduationByRole(settings.graduationByRole);
                setBaseline(JSON.stringify(settings.graduationByRole));
            })
            .catch(() => {
                toast.error("Impossible de charger les paramètres.");
            })
            .finally(() => setIsLoading(false));
    }, []);

    const isDirty = useMemo(() => {
        if (baseline === null || !graduationByRole) return false;
        return JSON.stringify(graduationByRole) !== baseline;
    }, [baseline, graduationByRole]);

    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);

    const updateRoleField = useCallback(
        <K extends keyof GraduationRoleThresholds>(
            role: RoleKey,
            field: K,
            value: GraduationRoleThresholds[K]
        ) => {
            setGraduationByRole((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    [role]: {
                        ...prev[role],
                        [field]: value,
                    },
                };
            });
        },
        []
    );

    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!graduationByRole) return false;

        setIsSaving(true);
        try {
            const saved = await patchAppSettings({
                graduationByRole,
            });
            if (!saved) {
                throw new Error("EMPTY_SETTINGS");
            }
            setGraduationByRole(saved.graduationByRole);
            setBaseline(JSON.stringify(saved.graduationByRole));
            toast.success("Paramètres enregistrés.");
            return true;
        } catch {
            toast.error("Échec de l'enregistrement.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [graduationByRole]);

    useEffect(() => {
        registerUnsavedGuard({ isDirty, save: handleSave });
        return () => registerUnsavedGuard(null);
    }, [registerUnsavedGuard, isDirty, handleSave]);

    return (
        <div className={styles.form}>
            <div className={styles.header}>
                {isMobile ? (
                    <Select
                        value="progression"
                        className={styles.selectTab}
                        onChanged={(value) =>
                            handleTabChange(
                                value === "tags" ? "" : (value as string)
                            )
                        }
                    >
                        <option value="tags">Tags</option>
                        {canReadSettings && (
                            <option value="progression">Progression</option>
                        )}
                        {canReadSettings && (
                            <option value="journal">Journal</option>
                        )}
                    </Select>
                ) : (
                    <span className={styles.form_header}>Progression</span>
                )}
                <Button
                    label={isSaving ? "Enregistrement…" : "Enregistrer"}
                    size="medium"
                    variant="primary"
                    onClick={() => {
                        void handleSave();
                    }}
                    disabled={isLoading || isSaving || !graduationByRole}
                />
            </div>

            {isLoading || !graduationByRole ? (
                <Loader />
            ) : (
                <div className={styles.sections}>
                    {(Object.keys(ROLE_LABELS) as RoleKey[]).map((role) => {
                        const config = graduationByRole[role];
                        return (
                            <div key={role} className={styles.section}>
                                <span className={styles.form_name}>
                                    Seuils {ROLE_LABELS[role]}
                                </span>
                                <div className={styles.fieldsGrid}>
                                    <label className={styles.field}>
                                        <span>Fenêtre (jours)</span>
                                        <Input
                                            type="number"
                                            value={String(config.lookbackDays)}
                                            onChanged={(value) =>
                                                updateRoleField(
                                                    role,
                                                    "lookbackDays",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </label>
                                    <label className={styles.field}>
                                        <span>Sessions min.</span>
                                        <Input
                                            type="number"
                                            value={String(config.minSessions)}
                                            onChanged={(value) =>
                                                updateRoleField(
                                                    role,
                                                    "minSessions",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </label>
                                    <label className={styles.field}>
                                        <span>Day win min. (%)</span>
                                        <Input
                                            type="number"
                                            value={String(config.minDayWin)}
                                            onChanged={(value) =>
                                                updateRoleField(
                                                    role,
                                                    "minDayWin",
                                                    Number(value)
                                                )
                                            }
                                        />
                                    </label>
                                </div>
                                <div className={styles.toggles}>
                                    <label className={styles.toggle}>
                                        <input
                                            type="checkbox"
                                            checked={config.requireFunding}
                                            onChange={(e) =>
                                                updateRoleField(
                                                    role,
                                                    "requireFunding",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        Exiger financé
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Progression;
